<script setup lang="ts">
  import { getMonthIndex, formatDate, getStartOfDay } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { CalendarSize, type DateTimestamp, MonthName } from './Calendar.types';
  import CalendarDay from './CalendarDay.vue';
  import CalendarWeek from './CalendarWeek.vue';
  import { Typo } from '@/components';

  interface CalendarTemplateProps {
    dates: DateTimestamp[];
    templateDate: DateTimestamp;
    size?: `${CalendarSize}`;
    range?: boolean;
    mobile?: boolean;
    double?: boolean;
    flexibleDays?: number | null;
    minDate?: Date | null;
    maxDate?: Date | null;
  }

  const props = withDefaults(defineProps<CalendarTemplateProps>(), {
    size: CalendarSize.M,
    range: false,
    mobile: false,
    double: false,
    minDate: null,
    maxDate: null,
    flexibleDays: null,
  });

  interface CalendarTemplateEmits {
    (e: 'click', value: DateTimestamp): void;
    (e: 'hover', value: DateTimestamp): void;
  }

  const today = getStartOfDay(Date.now());

  const emit = defineEmits<CalendarTemplateEmits>();

  const onClick = (day: DateTimestamp) => {
    emit('click', day);
  };

  const onHover = (day: DateTimestamp) => {
    emit('hover', day);
  };

  const monthText = computed(() => {
    const month = getMonthIndex(new Date(props.templateDate));

    return `${Object.values(MonthName)[month]} ${formatDate(props.templateDate, 'yyyy')}`;
  });
</script>

<template>
  <div
    :class="['tl-calendar-template', `tl-calendar-template_size-${size}`, { 'tl-calendar-template_mobile': mobile }]"
  >
    <CalendarWeek v-if="!mobile" :size="size" class="tl-calendar-template__week" />

    <Typo v-if="mobile && double" variant="body-l-700" class="tl-calendar-template__month">
      {{ monthText }}
    </Typo>
    <div class="tl-calendar-template__days">
      <CalendarDay
        v-for="date in dates"
        :key="date"
        :day="date"
        :is-today="today === date"
        :template-date="templateDate"
        :flexible-days="flexibleDays"
        :min-date="minDate"
        :max-date="maxDate"
        :range="range"
        :mobile="mobile"
        :double="double"
        :size="size"
        @click="onClick(date)"
        @hover="onHover(date)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tl-calendar-template {
    $root: &;

    &_mobile {
      padding: var(--spacing-s) var(--spacing-l);

      &:last-child {
        border-top: 1px dashed var(--color-text-tertiary);
      }
    }

    &_size-l {
      padding: var(--spacing-s) 0 var(--spacing-m) 0;
      #{$root}__days {
        grid-template-columns: repeat(7, 54px);
      }
    }

    &_size-m {
      padding: var(--spacing-s) 0 var(--spacing-l) 0;
    }

    &__week {
      border-bottom: 1px solid var(--color-text-tertiary);
      margin-bottom: var(--spacing-s);
    }

    &__month {
      text-align: center;
    }

    &__days {
      align-items: center;
      display: grid;
      gap: var(--spacing-xxxs);
      grid-template-columns: repeat(7, 42px);
    }
  }
</style>
